<div class="case">
  <a class="back" routerLink="/admin/abuse">← Abuse board</a>

  <div class="loading" *ngIf="loading"><mat-spinner diameter="28"></mat-spinner></div>
  <div class="notice bad" *ngIf="error">{{ error }}</div>
  <div class="notice ok" *ngIf="notice">{{ notice }}</div>

  <ng-container *ngIf="!loading && case as c">
    <div class="head">
      <div>
        <h2>{{ c.profile?.name || 'Unknown account' }}</h2>
        <p class="sub">
          {{ c.profile?.email || 'no email on file' }} ·
          <span class="mono">{{ c.uid }}</span>
        </p>
        <p class="sub" *ngIf="c.profile?.createdAt">
          Signed up {{ c.profile?.createdAt | date: 'd MMM yyyy, HH:mm' }}
        </p>
      </div>
      <div class="pills">
        <span class="pill" [attr.data-status]="c.banned ? 'banned' : (c.alertLevel !== 'none' ? 'flagged' : 'clear')">
          {{ c.banned ? (c.autoDisabled ? 'Auto-banned' : 'Banned') : (c.alertLevel === 'flagged' ? 'Needs review' : 'Clear') }}
        </span>
        <span class="pill" data-status="flagged" *ngIf="c.personaMatch">Persona match</span>
        <span class="pill" data-status="banned" *ngIf="c.identity?.nameMismatch">Name mismatch</span>
        <span class="pill" data-status="flagged" *ngIf="c.probation">On probation</span>
        <span class="pill" data-status="clear" *ngIf="c.chatsPurgedAt">Chats purged</span>
      </div>
    </div>

    <section class="panel appeal" *ngIf="openAppeal as a">
      <div class="appeal-head">
        <h3>They have appealed</h3>
        <span class="pill" [attr.data-status]="'flagged'">Waiting since {{ a.createdAt | date: 'd MMM, HH:mm' }}</span>
      </div>
      <p class="meta" *ngIf="!a.withinWindow">
        This ban was already more than 30 days old when they wrote. Still worth
        answering — it is just not one of the fresh ones.
      </p>

      <h4>What brought them to NX2U</h4>
      <blockquote>{{ a.broughtYou || '—' }}</blockquote>
      <h4>Who they were reaching out to, and what about</h4>
      <blockquote>{{ a.reachingOut || '—' }}</blockquote>
      <h4 *ngIf="a.placeYou">Anything that places them</h4>
      <blockquote *ngIf="a.placeYou">{{ a.placeYou }}</blockquote>
      <p class="meta">Reply goes to {{ a.email }}</p>

      <div class="row">
        <button mat-flat-button color="primary" (click)="restoreAppeal()" [disabled]="busy">
          {{ busy === 'restore' ? 'Working…' : 'Restore & reply' }}
        </button>
        <button mat-stroked-button color="warn" (click)="upholdAppeal()" [disabled]="busy">
          {{ busy === 'uphold' ? 'Working…' : 'Uphold & reply' }}
        </button>
      </div>
      <p class="meta">
        Each opens a confirm dialog first, showing exactly what will be sent
        and letting it be edited before it goes.
      </p>
    </section>

    <section class="panel" *ngIf="pastAppeals.length">
      <h3>Earlier appeals ({{ pastAppeals.length }})</h3>
      <ul class="people">
        <li *ngFor="let a of pastAppeals">
          <span class="title">{{ a.status === 'restored' ? 'Restored' : 'Upheld' }}
            — {{ a.decidedAt | date: 'd MMM yyyy' }}</span>
          <span class="meta">by {{ a.decidedBy }} · they wrote: {{ a.reachingOut || a.broughtYou }}</span>
        </li>
      </ul>
    </section>

    <section class="panel dossier">
      <div class="appeal-head">
        <h3>Who they are</h3>
        <button mat-stroked-button (click)="openProfile()" [disabled]="loadingProfile">
          {{ loadingProfile ? 'Opening…' : 'Open full profile' }}
        </button>
      </div>

      <div class="mismatch" *ngIf="c.identity?.nameMismatch">
        Signs in as <b>{{ c.identity.authName }}</b> but calls itself
        <b>{{ c.identity.profileName }}</b>. A real member's two names normally
        agree; a borrowed login under an invented identity is what they look
        like when they do not.
      </div>

      <div class="facts">
        <div class="fact">
          <div class="fk">Lifetime activity</div>
          <div class="fv" [class.cold]="dormantSinceSignup">{{ lifetimeSummary }}</div>
          <div class="meta" *ngIf="dormantSinceSignup">
            Existed over a month and never messaged anybody. That is a dormant or
            frozen account, not a clean record — a locked-out account cannot act,
            so its silence proves nothing.
          </div>
        </div>
        <div class="fact" *ngIf="c.dossier as d">
          <div class="fk">Profile</div>
          <div class="fv">
            {{ d.role || 'no role' }}<span *ngIf="d.companyName"> at {{ d.companyName }}</span>
          </div>
          <div class="meta" *ngIf="d.missingFields.length">
            Missing: {{ d.missingFields.join(', ') }}
          </div>
          <div class="meta" *ngIf="!d.missingFields.length">Complete</div>
          <div class="meta" *ngIf="d.linkedinUrl">
            <a [href]="d.linkedinUrl" target="_blank" rel="noopener">LinkedIn</a>
          </div>
          <div class="meta" *ngIf="d.originalDisplayName">
            First signed up as "{{ d.originalDisplayName }}"
          </div>
        </div>
        <div class="fact" *ngIf="c.dossier as d">
          <div class="fk">Events ({{ d.events.length }})</div>
          <div class="fv" *ngIf="!d.events.length">None — arrived on their own</div>
          <ul class="people" *ngIf="d.events.length">
            <li *ngFor="let e of d.events">
              <app-event-link [eventId]="e.id" [title]="e.title || e.tag"></app-event-link>
            </li>
          </ul>
          <div class="meta" *ngIf="d.events.length">
            An organiser here can vouch for them, or say they never arrived.
          </div>
        </div>
      </div>

      <h4 *ngIf="timeline.length">Timeline</h4>
      <ul class="people timeline" *ngIf="timeline.length">
        <li *ngFor="let t of timeline">
          <span class="title">{{ t.at | date: 'd MMM yyyy, HH:mm' }}</span>
          <span class="meta">{{ t.what }}</span>
        </li>
      </ul>
    </section>

    <section class="panel posts" *ngIf="c.posts?.length">
      <h3>On the Updates feed ({{ c.posts.length }})</h3>
      <p class="meta" *ngIf="c.banned">
        Hidden from the feed while the account is blocked; unblocking shows them again.
        Delete only what should never come back.
      </p>
      <p class="meta" *ngIf="!c.banned">
        Live for every member right now. Blocking the account hides all of them at once.
      </p>
      <app-feed-post-list
        [posts]="c.posts"
        [deletable]="true"
        [busy]="busy === 'delete-post'"
        (remove)="deletePost($event)"
      ></app-feed-post-list>
    </section>

    <div class="grid">
      <section class="panel">
        <h3>Why it was flagged</h3>
        <ul class="reasons" *ngIf="c.reasons.length"><li *ngFor="let r of c.reasons">{{ r }}</li></ul>
        <p class="meta" *ngIf="!c.reasons.length">
          No guard reasons — this account was blocked from the admin endpoint, not by the guard.
        </p>
        <p class="meta" *ngIf="c.bannedAt">
          Banned {{ c.bannedAt | date: 'd MMM yyyy, HH:mm' }} · source {{ c.banSource }}
        </p>

        <h3 *ngIf="c.sampleMessage">Opening message</h3>
        <blockquote *ngIf="c.sampleMessage">{{ c.sampleMessage }}</blockquote>
      </section>

      <section class="panel">
        <h3>Who it reached ({{ c.recipients.length }})</h3>
        <p class="signal" [class.cold]="noneReplied">
          Replies: {{ replySummary }}
        </p>
        <p class="signal warm" *ngIf="hasPeerChats">
          {{ peerSummary }}
        </p>
        <p class="meta" *ngIf="allPeerChats">
          Everyone they wrote to is somebody they are attending the same thing as.
          Introducing yourself around your own event looks like a blast by volume
          and by repetition, and is the app working.
        </p>
        <p class="meta">
          Real outreach starts conversations; a script sent to a list does not.
          This counts the guard's last 24 hours only — see lifetime activity above
          for the whole picture.
        </p>
        <p class="meta" *ngIf="!c.recipients.length">
          No recipients in the guard's window. The window prunes after 24 hours,
          so an older case shows none even when chats still exist.
        </p>
        <ul class="people">
          <li *ngFor="let p of c.recipientProfiles">
            <span class="title">
              <app-person-link [uid]="p.uid" [name]="p.name || p.uid" (failed)="error = $event"></app-person-link>
            </span>
            <span class="meta">{{ p.email || p.uid }}</span>
          </li>
        </ul>

        <h3>Reports ({{ c.reports.length }})</h3>
        <p class="meta" *ngIf="!c.reports.length">Nobody reported this account.</p>
        <ul class="people">
          <li *ngFor="let r of c.reports">
            <span class="title">{{ r.reason || 'no reason given' }}</span>
            <span class="meta">by
              <app-person-link [uid]="r.reportedBy" [name]="r.reportedBy" (failed)="error = $event"></app-person-link>
              · {{ r.filedAt ? (r.filedAt | date: 'd MMM, HH:mm') : '—' }}</span>
            <span class="meta" *ngIf="r.details">{{ r.details }}</span>
          </li>
        </ul>

        <h3>Blocked by ({{ c.blockedBy.blockers.length }})</h3>
        <p class="meta" *ngIf="!c.blockedBy.known">
          Couldn't be read. This is our failure, not a fact about them — decide
          on the other signals here and check the function logs.
        </p>
        <p class="meta" *ngIf="c.blockedBy.known && !c.blockedBy.blockers.length">
          Nobody blocked this account.
        </p>
        <p class="meta" *ngIf="c.blockedBy.blockers.length">
          Blocking asks nobody for anything and tells nobody, so people do it
          where they would not file a report. Several here, from people who
          never wrote back, says more than one report does.
        </p>
        <ul class="people">
          <li *ngFor="let b of c.blockedBy.blockers">
            <span class="title">
              <app-person-link [uid]="b" [name]="b" (failed)="error = $event"></app-person-link>
            </span>
          </li>
        </ul>
      </section>
    </div>

    <section class="panel actions">
      <h3>Actions</h3>
      <div class="row">
        <button mat-flat-button color="warn" *ngIf="!c.banned" (click)="block()" [disabled]="busy">
          {{ busy === 'block' ? 'Working…' : 'Block account' }}
        </button>
        <button mat-stroked-button *ngIf="c.banned" (click)="unblock()" [disabled]="busy">
          {{ busy === 'unblock' ? 'Working…' : 'Unblock account' }}
        </button>
        <button mat-stroked-button (click)="dismiss()" [disabled]="busy">
          {{ busy === 'dismiss' ? 'Working…' : 'Dismiss as false positive' }}
        </button>
      </div>
      <p class="meta">
        Each of these opens a confirm dialog first — nothing here fires from a
        single click.
      </p>

      <h3 class="danger-title">Delete chat traces</h3>
      <p class="meta">
        Archives every chat this account took part in to Cloud Storage — full
        documents, server timestamps, SHA-256 checksum — and then deletes them
        from Firestore, clearing the unread badges on both sides. This cannot be
        undone from here. Type <b>delete</b> to enable the button.
      </p>
      <div class="row">
        <input
          class="confirm"
          type="text"
          placeholder="delete"
          [value]="purgeConfirmation"
          (input)="onConfirmationInput($event)"
          [disabled]="purging"
        />
        <button mat-flat-button color="warn" (click)="purgeChats()" [disabled]="!canPurge || purging">
          {{ purging ? 'Archiving…' : 'Archive and delete chats' }}
        </button>
      </div>
      <p class="meta" *ngIf="purgeProgress && purging">
        {{ purgeProgress.chats }} chats, {{ purgeProgress.messages }} messages so far…
      </p>
    </section>
  </ng-container>
</div>
